import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getSource, useSources, type MediaItem } from "../../sources";
import { lastSeenIfOffline, useServers } from "../../lib/servers";
import { useMetadataJobs } from "../../lib/activityHub";
import {
  CARD_MIN,
  GRID_GAP_X,
  GRID_PAGE,
  columnsFor,
  pagesFor,
  rowEstimate,
  rowsFor,
  slotRange,
} from "../../lib/windowedList";
import {
  activeFilterCount,
  clearFilters,
  filterKey,
  librarySearchSchema,
  orderOf,
  toFilterQuery,
  toItemsQuery,
  viewKey,
  withFlag,
  type LibrarySearch,
} from "../../lib/libraryFilters";
import { buildLetterIndex, letterForSlot, railEntries, rowForLetter } from "../../lib/letterIndex";
import { useWatchSnapshot } from "../../components/media";
import { MediaCard, PlaceholderCard } from "../../components/library/media-card";
import { PosterGrid } from "../../components/library/poster-grid";
import { LibraryToolbar } from "../../components/library/library-toolbar";
import { LetterRail } from "../../components/library/letter-rail";
import { EmptyState, LoadingState, NotFoundState, SkeletonGrid } from "../../components/page-state";
import { ServerUnavailableState } from "../../components/server-unavailable";
import { FilmstripIcon, SlidersIcon, TvIcon } from "../../components/icons";

export const Route = createFileRoute("/_authed/library/$sourceId/$libraryId")({
  // Sort, direction, seed, the title query, three flags and nine list
  // facets (lib/libraryFilters.ts) — absent = off, so the plain library
  // URL stays clean and every view is shareable and Back-safe.
  validateSearch: librarySearchSchema,
  component: Library,
});

const GRID_GAP_Y = 28; // gap-y-7 — each virtual row carries it as padding
const PAGE_STALE_MS = 30_000;
const FACETS_STALE_MS = 300_000;
/** Between the floating bar's bottom edge and a row the rail jumped to. */
const BAR_BREATH = 12;
const fmt = (n: number) => new Intl.NumberFormat().format(n);

/**
 * The library grid, Plex-style: one virtual scroller sized from the
 * server's total, fetching only the 100-row pages whose rows are on
 * screen (plus one ahead). A 95K-item library keeps ~40 cards in the DOM
 * and costs one page per viewport; every page is its own query, so the
 * hub bridge's ["library", serverId] invalidation refetches just the
 * pages that are mounted. Sorted by title, the A–Z rail on the right
 * edge jumps by prefix-summed per-letter counts from the server.
 */
function Library() {
  const { sourceId, libraryId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  // Drive/await the source registry so a direct load doesn't blank before
  // the server's ServerSource is registered.
  const { isLoading: sourcesLoading } = useSources();
  const source = getSource(sourceId);
  // Presence row for the offline gate (rides the same ["resources"] cache).
  const { servers } = useServers();
  const row = servers.find((s) => s.id === sourceId);
  const watchVersion = useWatchSnapshot();
  // Posters the metadata agent is on right now (Plex's ring + tag).
  const busy = useMetadataJobs(sourceId);

  const enabled = Boolean(source) && (!row || row.online);
  const owned = Boolean(row?.owned);

  // Every toolbar and popup change is a navigation: the URL is the state.
  // The router's default resetScroll returns the window to the top.
  const apply = useCallback(
    (next: LibrarySearch, opts?: { replace?: boolean }) => {
      void navigate({ search: next, replace: opts?.replace });
    },
    [navigate],
  );

  // The library itself carries the header facts (title, type, live count).
  const libs = useQuery({
    queryKey: ["libraries", sourceId],
    staleTime: 60_000,
    queryFn: () => source!.getLibraries(),
    enabled,
  });
  const lib = libs.data?.find((l) => l.id === libraryId);

  const order = orderOf(search);
  const fKey = filterKey(search);
  const vKey = viewKey(search);
  const itemsQuery = useMemo(() => toItemsQuery(search), [search]);
  const filterQuery = useMemo(() => toFilterQuery(search), [search]);

  // Page keys keep "library", sourceId in positions 0–1: every
  // invalidation in the app is a prefix match on those two.
  const pageKey = (page: number) => ["library", sourceId, libraryId, vKey, fKey, page] as const;
  const fetchPage = (page: number) =>
    source!.getLibraryItems(libraryId, { ...itemsQuery, offset: page * GRID_PAGE, limit: GRID_PAGE });

  // Page 0 is always mounted: it carries the total that sizes the grid.
  // keepPreviousData holds the old view on screen until the new one lands.
  const first = useQuery({
    queryKey: pageKey(0),
    queryFn: () => fetchPage(0),
    enabled,
    staleTime: PAGE_STALE_MS,
    placeholderData: keepPreviousData,
  });
  const total = first.data?.total ?? 0;

  // The rail's counts under the current filters (title order only) and
  // the popup's facets. Both are keyed OUTSIDE ["library", …]: the hub's
  // in-place item patch assumes everything under that prefix is a page.
  // null = a server before the browser routes → rail / facets hidden.
  const letters = useQuery({
    queryKey: ["library-letters", sourceId, libraryId, fKey],
    queryFn: async () => (await source!.getLibraryLetters!(libraryId, filterQuery)) ?? null,
    enabled: enabled && search.sort === "title" && Boolean(source?.getLibraryLetters),
    staleTime: PAGE_STALE_MS,
    placeholderData: keepPreviousData,
  });
  const facets = useQuery({
    queryKey: ["library-facets", sourceId, libraryId],
    queryFn: async () => (await source!.getLibraryFacets!(libraryId)) ?? null,
    enabled: enabled && Boolean(source?.getLibraryFacets),
    staleTime: FACETS_STALE_MS,
  });

  // Marking something watched moves it across the Unwatched / In progress
  // filters: refetch the view when the watch state changes under it.
  const watchRef = useRef(watchVersion);
  useEffect(() => {
    if (watchRef.current === watchVersion) return;
    watchRef.current = watchVersion;
    if (!search.unwatched && !search.inProgress) return;
    void qc.invalidateQueries({ queryKey: ["library", sourceId, libraryId] });
    void qc.invalidateQueries({ queryKey: ["library-letters", sourceId, libraryId] });
    void qc.invalidateQueries({ queryKey: ["library-facets", sourceId, libraryId] });
  }, [watchVersion, search.unwatched, search.inProgress, qc, sourceId, libraryId]);

  // Column count and row height follow the grid's width (and Tailwind's
  // sm breakpoint, which switches the card minimum); the scroll margin is
  // the grid's offset in the page for the window virtualizer; the
  // clearance is where content stops being hidden by the floating bar.
  // Callback refs, not useRef: the first render is the loading state
  // (source registry, presence), so the grid mounts later and the
  // observer must attach when it does — a once-on-mount effect never saw
  // it and left every row one column wide. The header is observed too:
  // its height moves the grid without resizing it (an applied-filter row
  // appearing, a wrapping title) and a stale scrollMargin would put every
  // jump off by that much.
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const [headEl, setHeadEl] = useState<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState({ cols: 1, width: 0, top: 0, clearance: 80 });
  useLayoutEffect(() => {
    const el = gridEl;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const cardMin = window.matchMedia("(min-width: 640px)").matches
        ? CARD_MIN.wide
        : CARD_MIN.narrow;
      const bar = document.querySelector("[data-rago-bar]")?.getBoundingClientRect().bottom ?? 68;
      const next = {
        cols: columnsFor(rect.width, cardMin, GRID_GAP_X),
        width: rect.width,
        top: rect.top + window.scrollY,
        clearance: Math.round(bar + BAR_BREATH),
      };
      setLayout((prev) =>
        prev.cols === next.cols &&
        prev.width === next.width &&
        prev.top === next.top &&
        prev.clearance === next.clearance
          ? prev
          : next,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (headEl) ro.observe(headEl);
    return () => ro.disconnect();
  }, [gridEl, headEl]);
  const { cols, width, top, clearance } = layout;
  const rows = rowsFor(total, cols);
  const estimate = rowEstimate(width, cols, GRID_GAP_X, GRID_GAP_Y);

  const virtualizer = useWindowVirtualizer({
    count: rows,
    estimateSize: () => estimate,
    overscan: 2,
    scrollMargin: top,
    scrollPaddingStart: clearance,
  });
  // estimateSize is not one of the virtualizer's measurement-cache keys:
  // when the grid's width (so the row estimate) changes, drop the cache so
  // rows the user has not reached yet are laid out from the new estimate.
  useEffect(() => {
    virtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate]);
  const virtualRows = virtualizer.getVirtualItems();
  const firstRow = virtualRows[0]?.index ?? 0;
  const lastRow = virtualRows[virtualRows.length - 1]?.index ?? -1;
  const [firstSlot, lastSlot] = slotRange(firstRow, lastRow, cols, total);
  // The pages on screen plus the next one, so a scroll never waits on the
  // network for the row just below (Plex prefetches the next window too).
  const pages = pagesFor(firstSlot, lastSlot < 0 ? -1 : Math.min(total - 1, lastSlot + GRID_PAGE));
  const morePages = pages.filter((p) => p !== 0);
  const pageQueries = useQueries({
    queries: morePages.map((page) => ({
      queryKey: pageKey(page),
      queryFn: () => fetchPage(page),
      enabled: enabled && first.isSuccess,
      staleTime: PAGE_STALE_MS,
      placeholderData: keepPreviousData,
    })),
  });
  const pageItems = new Map<number, MediaItem[]>();
  if (first.data) pageItems.set(0, first.data.items);
  morePages.forEach((page, i) => {
    const data = pageQueries[i]?.data;
    if (data) pageItems.set(page, data.items);
  });
  const slotItem = (i: number) => pageItems.get(Math.floor(i / GRID_PAGE))?.[i % GRID_PAGE];

  // The rail: prefix-summed buckets in the current direction; a jump
  // scrolls the letter's first row under the bar (scrollPaddingStart);
  // the active letter is the first row whose bottom edge clears the bar.
  const letterIndex = useMemo(
    () => (letters.data ? buildLetterIndex(letters.data, order) : null),
    [letters.data, order],
  );
  const entries = useMemo(() => (letterIndex ? railEntries(letterIndex) : []), [letterIndex]);
  // The letter a jump asked for, held while its landing row is the one
  // under the bar: that row usually opens with the previous letter's last
  // titles (…Gunslingers, Gutter | H Is for Happiness…), and the first-card
  // rule below would light G right after a jump to H (Kenny's report).
  // Scrolling on to another row hands the highlight back to the rule.
  const [jumped, setJumped] = useState<{ key: string; row: number } | null>(null);
  useEffect(() => setJumped(null), [letterIndex, cols]);
  const jumpTo = useCallback(
    (key: string) => {
      if (!letterIndex) return;
      const r = rowForLetter(letterIndex, key, cols);
      if (r == null) return;
      setJumped({ key, row: r });
      virtualizer.scrollToIndex(r, { align: "start" });
    },
    [letterIndex, cols, virtualizer],
  );
  const scrollY = virtualizer.scrollOffset ?? 0;
  const firstVisible = virtualRows.find((vr) => vr.end > scrollY + clearance) ?? virtualRows[0];
  const activeLetter =
    jumped && firstVisible && firstVisible.index === jumped.row
      ? jumped.key
      : letterIndex && firstVisible
        ? letterForSlot(letterIndex, firstVisible.index * cols)
        : null;

  if (!source) {
    if (sourcesLoading) return <LoadingState />;
    return <NotFoundState />;
  }

  const offline = Boolean(row && !row.online);
  const filtersOn = activeFilterCount(search);
  const onlyUnmatched = filtersOn === 1 && Boolean(search.unmatched);
  const railVisible = search.sort === "title" && Boolean(letterIndex) && total > 0 && !offline && !first.isError;
  const libraryTotal = lib?.itemCount;
  const noun = lib?.type === "show" ? "show" : "title";
  const countText = first.data
    ? filtersOn > 0 && libraryTotal != null
      ? `${fmt(total)} of ${fmt(libraryTotal)} ${noun}s`
      : `${fmt(total)} ${noun}${total === 1 ? "" : "s"}`
    : libraryTotal != null
      ? `${fmt(libraryTotal)} ${noun}${libraryTotal === 1 ? "" : "s"}`
      : null;
  const unmatchedCount = facets.data?.unmatched ?? lib?.unmatchedCount ?? 0;
  const glyph = lib?.type === "show" ? <TvIcon size={22} /> : <FilmstripIcon size={22} />;

  return (
    <div className={`rago-page px-6 pb-10 sm:px-10 ${railVisible ? "pr-12 sm:pr-16" : ""}`}>
      <div ref={setHeadEl} className="mb-6 flex flex-col gap-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--rago-radius-m)] bg-[var(--rago-bg-3)] text-[var(--rago-accent)]">
              {glyph}
            </span>
            <div className="min-w-0">
              <h1 className="rago-display truncate text-3xl leading-tight sm:text-4xl">
                {lib?.title ?? source.name}
              </h1>
              <p className="mt-1 truncate text-[13px] text-[var(--rago-text-3)]">
                {source.name}
                {countText ? ` · ${countText}` : ""}
                {owned && unmatchedCount > 0 && !search.unmatched ? (
                  <>
                    {" · "}
                    <button
                      type="button"
                      title="Show only items without a metadata match"
                      onClick={() => apply(withFlag(search, "unmatched", true))}
                      className="cursor-pointer select-none rounded text-[var(--rago-accent)] transition-colors hover:text-[var(--rago-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--rago-ring)] focus-visible:outline-none"
                    >
                      {fmt(unmatchedCount)} unmatched
                    </button>
                  </>
                ) : null}
              </p>
            </div>
          </div>
        </div>
        <LibraryToolbar
          search={search}
          onChange={apply}
          facets={facets.data ?? undefined}
          libraryType={lib?.type}
          owned={owned}
          total={total}
          libraryTotal={libraryTotal}
          busy={first.isPlaceholderData}
        />
      </div>

      {/* The measured element is always mounted so the layout effect sees it
          from the first render, whatever state renders inside. */}
      <div ref={setGridEl}>
        {offline ? (
          <ServerUnavailableState
            compact
            serverId={sourceId}
            serverName={row?.name ?? source.name}
            owned={row?.owned}
            lastSeenAt={row ? lastSeenIfOffline(row) : null}
          />
        ) : first.isLoading ? (
          <SkeletonGrid />
        ) : first.isError ? (
          <ServerUnavailableState
            compact
            serverId={sourceId}
            serverName={row?.name ?? source.name}
            owned={row?.owned}
          />
        ) : total === 0 ? (
          onlyUnmatched ? (
            <p className="py-16 text-center text-sm text-[var(--rago-text-3)]">
              Every item in this library is matched.
            </p>
          ) : filtersOn > 0 ? (
            <EmptyState
              compact
              icon={<SlidersIcon size={22} />}
              title="Nothing matches these filters"
              action={
                <button type="button" className="rago-btn rago-btn-quiet rago-btn-sm" onClick={() => apply(clearFilters(search))}>
                  Clear filters
                </button>
              }
            >
              Try fewer filters, or a different title.
            </EmptyState>
          ) : (
            <EmptyState compact icon={glyph} title="This library is empty">
              Nothing has been scanned into it yet.
            </EmptyState>
          )
        ) : (
          <div
            className="relative transition-opacity duration-150"
            aria-busy={first.isPlaceholderData || undefined}
            style={{ height: virtualizer.getTotalSize(), opacity: first.isPlaceholderData ? 0.55 : 1 }}
          >
            {virtualRows.map((vr) => (
              <div
                key={vr.key}
                data-index={vr.index}
                ref={virtualizer.measureElement}
                className="absolute top-0 left-0 w-full"
                style={{
                  paddingBottom: GRID_GAP_Y,
                  transform: `translateY(${vr.start - virtualizer.options.scrollMargin}px)`,
                }}
              >
                <PosterGrid cols={cols}>
                  {Array.from({ length: cols }, (_, c) => {
                    const i = vr.index * cols + c;
                    if (i >= total) return null;
                    const item = slotItem(i);
                    return item ? (
                      <MediaCard key={item.id} item={item} source={source} busy={busy.has(item.id)} />
                    ) : (
                      <PlaceholderCard key={`slot-${i}`} />
                    );
                  })}
                </PosterGrid>
              </div>
            ))}
          </div>
        )}
      </div>
      {railVisible ? (
        <LetterRail
          entries={entries}
          active={activeLetter}
          disabled={first.isPlaceholderData || letters.isPlaceholderData}
          onJump={jumpTo}
        />
      ) : null}
    </div>
  );
}
